import { FORMAT_LABELS, SOURCE_LABELS, STAGES, type Stage } from '@jt/core';
import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { useCreateApplication, useTodayDate } from '../api/hooks';
import { useAi } from '../drafts/useDraft';
import { PasteForm, UrlForm } from '../drafts/PasteForm';
import { useUI } from './overlays';
import { Icon } from './ui';

type UI = ReturnType<typeof useUI>;

type Mode = 'manual' | 'text' | 'url';

export function openNewApplication(ui: UI, opts: { stage?: Stage; mode?: Mode } = {}) {
  ui.openSheet({
    title:
      opts.mode === 'text' ? 'Вставить текст' : opts.mode === 'url' ? 'Вакансия по ссылке' : 'Новая вакансия',
    body: <NewApplication stage={opts.stage} mode={opts.mode ?? 'manual'} />,
  });
}

function NewApplication({ stage, mode: initial }: { stage?: Stage; mode: Mode }) {
  const ai = useAi();
  const [mode, setMode] = useState<Mode>(ai ? initial : 'manual');
  return (
    <>
      {ai && (
        <div className="seg" role="tablist" style={{ marginBottom: 14 }}>
          <button role="tab" aria-selected={mode === 'manual'} onClick={() => setMode('manual')}>
            Вручную
          </button>
          <button role="tab" aria-selected={mode === 'text'} onClick={() => setMode('text')}>
            <Icon name="sparkles" size={16} /> Из текста
          </button>
          <button role="tab" aria-selected={mode === 'url'} onClick={() => setMode('url')}>
            <Icon name="link" size={16} /> URL
          </button>
        </div>
      )}
      {mode === 'text' && ai ? (
        <PasteForm targetId={null} />
      ) : mode === 'url' && ai ? (
        <UrlForm targetId={null} />
      ) : (
        <ManualForm stage={stage} />
      )}
    </>
  );
}

function ManualForm({ stage }: { stage?: Stage }) {
  const ui = useUI();
  const today = useTodayDate();
  const create = useCreateApplication();
  const [err, setErr] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState('');

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const v = (k: string) => String(f.get(k) ?? '').trim();
    if (!v('company') && !v('role')) {
      setErr({ company: 'Укажите хотя бы компанию или роль' });
      return;
    }
    const contact = { name: v('cname'), role: v('crole'), email: v('cemail'), linkedinUrl: v('clinkedin') };
    try {
      const full = await create.mutateAsync({
        company: v('company'),
        role: v('role'),
        source: v('source') || null,
        stage: v('stage') || 'applied',
        salaryRange: v('salary'),
        format: v('format') || null,
        location: v('location'),
        appliedAt: v('date') || undefined,
        url: v('url'),
        notes: v('notes'),
        ...(contact.name || contact.email || contact.linkedinUrl ? { contact } : {}),
      });
      ui.closeSheet();
      ui.openApp(full.id);
      ui.toast('Вакансия добавлена');
    } catch (x) {
      if (x instanceof ApiError) {
        setErr(x.fields);
        setMsg(Object.keys(x.fields).length ? '' : x.message);
      } else setMsg('Не сохранилось. Повторите.');
    }
  };

  const field = (name: string, label: string, props: Record<string, unknown> = {}, cls = '') => (
    <label className={`f ${cls}`}>
      {label}
      <input name={name} aria-invalid={!!err[name] || undefined} {...props} />
      {err[name] && <span className="fnote err">{err[name]}</span>}
    </label>
  );

  return (
    <form onSubmit={submit} noValidate>
      <section className="sec">
        <h3>Вакансия</h3>
        <div className="grid">
          {field('company', 'Компания', { autoComplete: 'off', autoFocus: true })}
          {field('role', 'Роль', { defaultValue: 'Senior QA Engineer' })}
          <label className="f">
            Источник
            <select name="source" defaultValue="LinkedIn">
              {Object.entries(SOURCE_LABELS).map(([k, t]) => (
                <option key={k} value={k}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label className="f">
            Статус
            <select name="stage" defaultValue={stage ?? 'applied'}>
              {STAGES.filter((s) => !s.closed).map((s) => (
                <option key={s.key} value={s.key}>
                  {s.title}
                </option>
              ))}
            </select>
          </label>
          {field('salary', 'Зарплата / вилка', { placeholder: '€60–70k / год' })}
          <label className="f">
            Формат
            <select name="format" defaultValue="">
              <option value="">—</option>
              {Object.entries(FORMAT_LABELS).map(([k, t]) => (
                <option key={k} value={k}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          {field('location', 'Город / страна')}
          {field('date', 'Дата отклика', { type: 'date', defaultValue: today })}
          {field(
            'url',
            'Ссылка на вакансию',
            { type: 'url', inputMode: 'url', placeholder: 'https://' },
            'full',
          )}
        </div>
      </section>
      <section className="sec">
        <h3>Контакт</h3>
        <div className="grid">
          {field('cname', 'Имя')}
          {field('crole', 'Кто это', { placeholder: 'HR, рекрутер' })}
          {field('cemail', 'Email', { type: 'email' })}
          {field('clinkedin', 'LinkedIn', { placeholder: 'ссылка на профиль' })}
        </div>
      </section>
      <section className="sec">
        <div className="grid">
          <label className="f full">
            Заметки
            <textarea name="notes" rows={3} placeholder="Стек, требования, впечатления" />
          </label>
        </div>
      </section>
      {msg && (
        <p className="fnote err" role="alert">
          {msg}
        </p>
      )}
      <button className="btn primary" type="submit" disabled={create.isPending}>
        <Icon name="plus" /> Добавить вакансию
      </button>
    </form>
  );
}
